import React from 'react';

import PropTypes from 'prop-types';

import ModularBlock from 'components/ModularBlocks/ModularBlock';
import ModularSchema from 'components/ModularBlocks/ModularSchema';

import { BlogPostI } from 'interfaces/content-stack/blogPost';
import { BlockTypes, ModularBlockI } from 'interfaces/content-stack/modular-blocks/modularBlocks.types';

import modularBlockShape from 'shapes/modularBlockShape';

import { getBlockType } from 'utils/modularBlockUtils';

/**
 * Process the array of blocks to look for any block_group_start slices, and if any are found,
 * nest the appropriate blocks following the block_group_start as children of the block group.
 *
 * If block type is block_group_start, then we do a lookahead on the upcoming blocks
 * to determine how many blocks should be nested within this block group.
 *
 * If there is a block_group_end in the future, all blocks between the block_group_start and block_group_end
 * block types will be included as children nested under this block group.
 *
 * If there is no block_group_end in the future, then we assume that the block_group_start
 * applies only to the block immediately following it.
 */

function processBlockGroupBlocks(blocks: any) {
  const processedBlocks = [];

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const blockType = getBlockType(block);

    if (!blockType) {
      continue;
    }

    if (blockType === BlockTypes.blockGroupStart) {
      // Get subset of all slices following this block style slice.
      const upcomingBlocks = blocks.slice(i + 1);

      // Find the index in upcomingSlices array for the last slice included in this block style.
      const endIndex: number = getLastBlockIndexForBlockGroup(upcomingBlocks);

      // If the last slice is a block_style or block_style_end, we want to exclude it from our child slices.
      const lastBlockSliceIndex = isBlockGroupBoundaryBlockType(upcomingBlocks[endIndex]) ? endIndex - 1 : endIndex;

      // If last block slice index is < 0, then this block style applies to no slices and is skipped.
      if (lastBlockSliceIndex >= 0) {
        // Since "end" argument to slice is not included in the result, we add + 1 here.
        const childBlocks = upcomingBlocks.slice(0, lastBlockSliceIndex + 1);
        processedBlocks.push({ ...block, [blockType]: { ...block[blockType], childBlocks } });

        i += endIndex + 1;
      }
    } else if (blockType !== BlockTypes.blockGroupEnd) {
      // As long as the slice type isn't a rogue block_style_end type, we add this slice
      // to be included as the top level output.
      processedBlocks.push(block);
    }
  }
  return processedBlocks;
}

function getLastBlockIndexForBlockGroup(upcomingBlocks: any): number {
  // If endIndex is not found, that means that there was no block style end or followup block style,
  // so it will default to apply only to the next block.
  let endIndex = 0;

  for (let k = 0; k < upcomingBlocks.length; k++) {
    const upcomingBlock = upcomingBlocks[k];
    const blockType = getBlockType(upcomingBlock);
    if (!blockType) {
      continue;
    }

    if (blockType === BlockTypes.blockGroupStart) {
      // If we encounter another block_style before any block style end,
      // we default to apply to only the slice following the block style.
      endIndex = 0;
      break;
    } else if (blockType === BlockTypes.blockGroupEnd) {
      endIndex = k;
      break;
    }
  }

  return endIndex;
}

function isBlockGroupBoundaryBlockType(block: any) {
  const blockType = getBlockType(block);
  return blockType && (blockType === BlockTypes.blockGroupStart || blockType === BlockTypes.blockGroupEnd);
}

ModularBlocks.propTypes = {
  modularBlocks: PropTypes.arrayOf(modularBlockShape),
};

interface ModularBlocksProps {
  modularBlocks: ModularBlockI[];
  page?: any;
  blogPost?: {
    edges: {
      node: BlogPostI[];
    }[];
  }[];
  allArticles?: string;
}

function ModularBlocks({ modularBlocks, blogPost, ...props }: ModularBlocksProps) {
  if (!modularBlocks || modularBlocks.length === 0) {
    return null;
  }

  const processedBlocks = processBlockGroupBlocks(modularBlocks);

  return (
    <>
      <ModularSchema modularBlocks={processedBlocks} />
      {processedBlocks.map((modularBlock, index) => (
        <ModularBlock blogPost={blogPost} data={modularBlock} key={index} {...props} />
      ))}
    </>
  );
}

export default React.memo(ModularBlocks);
